import { Col, Row } from '@opendoor/atoms/box';
import { Combobox } from '@opendoor/atoms/combobox';
import { Field } from '@opendoor/atoms/field';
import { Text } from '@opendoor/atoms/text';
import type { ErrorKey, RangeOption } from 'components/marketplace/search/types';

import { buildRangeFilterOptions, filterIntOptions } from './utils';

const MIN_PLACEHOLDER: RangeOption = { label: 'No min', value: '0' };
const MAX_PLACEHOLDER: RangeOption = { label: 'No max', value: '500000000' };

const PriceRangeOptions: RangeOption[] = [
  {
    label: '$100,000',
    value: '10000000',
  },
  {
    label: '$200,000',
    value: '20000000',
  },
  {
    label: '$300,000',
    value: '30000000',
  },
  {
    label: '$400,000',
    value: '40000000',
  },
  {
    label: '$500,000',
    value: '50000000',
  },
  {
    label: '$600,000',
    value: '60000000',
  },
  {
    label: '$700,000',
    value: '70000000',
  },
  {
    label: '$800,000',
    value: '80000000',
  },
  {
    label: '$900,000',
    value: '90000000',
  },
  {
    label: '$1,000,000',
    value: '100000000',
  },
  {
    label: '$1,250,000',
    value: '125000000',
  },
  {
    label: '$1,500,000',
    value: '150000000',
  },
  {
    label: '$1,750,000',
    value: '175000000',
  },
];

export interface PriceFilterProps {
  minPrice?: string | null;
  maxPrice?: string | null;
  handleMinPriceChange: (value: string) => void;
  handleMaxPriceChange: (value: string) => void;
  handleError: (key: ErrorKey, val: string) => void; // used to notify parent component of error state
  errors?: Record<ErrorKey, string> | null;
}

export const PriceFilter = ({
  minPrice,
  maxPrice,
  handleMinPriceChange,
  handleMaxPriceChange,
  handleError,
  errors,
}: PriceFilterProps) => {
  const onMinPriceChange = (value: string) => {
    if (maxPrice && parseInt(value, 10) > parseInt(maxPrice, 10)) {
      handleError('minPrice', 'Min price must be less than max price');
      return;
    }
    handleError('minPrice', '');
    handleMinPriceChange(value);
  };

  const onMaxPriceChange = (value: string) => {
    if (minPrice && parseInt(value, 10) < parseInt(minPrice, 10)) {
      handleError('maxPrice', 'Max price must be greater than min price');
      return;
    }
    handleError('maxPrice', '');
    handleMaxPriceChange(value);
  };

  const minOptions = buildRangeFilterOptions(
    filterIntOptions(PriceRangeOptions, 'lt', maxPrice ?? undefined),
    MIN_PLACEHOLDER,
  );
  const maxOptions = buildRangeFilterOptions(
    filterIntOptions(PriceRangeOptions, 'gt', minPrice ?? undefined).concat({
      label: '$2,000,000+',
      value: '500000000',
    }),
    MAX_PLACEHOLDER,
  );

  return (
    <Col className="w-full gap-8">
      <Text as="p" className="text-label-xl">
        Price range
      </Text>
      <Row className="w-full justify-between">
        <Field className="w-[48%]" error={errors?.['minPrice'] || undefined}>
          <Combobox
            id="price-filter-min"
            aria-label="Minimum price"
            placeholder={MIN_PLACEHOLDER.label}
            options={minOptions}
            value={minOptions.find((option) => option.value === minPrice) ?? null}
            error={Boolean(errors?.['minPrice'])}
            size="sm"
            onValueChange={(option) => onMinPriceChange(option?.value ?? MIN_PLACEHOLDER.value)}
          />
        </Field>
        <Field className="w-[48%]" error={errors?.['maxPrice'] || undefined}>
          <Combobox
            id="price-filter-max"
            aria-label="Maximum price"
            placeholder={MAX_PLACEHOLDER.label}
            options={maxOptions}
            value={maxOptions.find((option) => option.value === maxPrice) ?? null}
            error={Boolean(errors?.['maxPrice'])}
            size="sm"
            onValueChange={(option) => onMaxPriceChange(option?.value ?? MAX_PLACEHOLDER.value)}
          />
        </Field>
      </Row>
    </Col>
  );
};
